// import { ListYellowBulletIcon } from "@/src/Asset";
import React, { useEffect, useState } from 'react';
import sanitizeHTML from '@/utils/sanatizeHtml';

import Accordion from '@/components/ui/components/Accordion';

import UpdateDateArticle from '../components/UpdateDateArticle';
// import Loader from "../loader/Loader";

import PageData from './PageData';

interface TabData {
  ___component: string;
  heading?: string;
  content: string;
  Content: string;
  id: string;
  outerHTML: string;
}

// PageDetailsTab Props Interface
interface PageDetailsTabProps {
  tabsData: TabData[];
  page: any;
  currentTab: string;
  loading?: boolean;
  sideBar?: boolean;
  id?: string;
}

// Move injectHeadingIds above PageDetailsTab so it can be used in both places
function injectHeadingIds(html: string, tabIndex: number) {
  if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return html;
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const headings = Array.from(doc.querySelectorAll('h2, h3, h4, h5, h6'));
  headings.forEach((heading, idx) => {
    heading.id = `tab-section-${tabIndex}-${idx}`;
  });
  return doc.body.innerHTML;
}

const PageDetailsTab = ({ tabsData, page, currentTab, loading, sideBar = true }: PageDetailsTabProps) => {
  const showTableOfContent = true;
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);
  // const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const [introContent, setIntroContent] = useState<string | null>(null);
  const [mainOverviewContent, setMainOverviewContent] = useState<string | null>(null);
  const [mainOverviewContentWithIds, setMainOverviewContentWithIds] = useState<string | null>(null);
  const [overviewHeading, setOverviewHeading] = useState<string | null>(null);
  const [isClient, setIsClient] = useState(false);

  const tableOfContentId = `${currentTab}-table-of-content`;
  const latestUpdateId = `${currentTab}-latest-update`;

  const handleAccordionToggle = (id: string) => {
    setOpenAccordionId((prev) => (prev === id ? null : id));
  };
  const [hydrated, setHydrated] = React.useState(false);
  React.useEffect(() => {
    setHydrated(true);
  }, []);

  // Helper function to find overview data
  const findOverviewData = (data: TabData[]) => {
    return data?.find(
      (item: any) =>
        item.__component === 'common.new-overview' ||
        item.__component === 'course.overview' ||
        item.__component === 'special.overview' ||
        item.__component === 'exam.overview',
    );
  };

  // Only run DOMParser logic on client
  useEffect(() => {
    setIsClient(true);

    const overviewData = findOverviewData(tabsData);
    if (!overviewData) {
      setIntroContent(null);
      setMainOverviewContent(null);
      setMainOverviewContentWithIds(null);
      setOverviewHeading(null);
      return;
    }
    const content = overviewData?.content || overviewData?.Content;
    if (!content || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
      setIntroContent(content || null);
      setMainOverviewContent('');
      setMainOverviewContentWithIds(null);
      setOverviewHeading(null);
      return;
    }

    // DOMParser logic
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    let introContentHtml = '';
    let mainContentHtml = '';
    let currentNode = doc.body.firstChild;
    let foundHeading = false;
    let firstHeadingText = null;

    while (currentNode) {
      const nextNode = currentNode.nextSibling;
      const nodeName = currentNode.nodeName.toLowerCase();

      if ((nodeName === 'h2' || nodeName === 'h3') && !foundHeading) {
        foundHeading = true;
        firstHeadingText = currentNode.textContent;
      }

      const nodeContent = currentNode instanceof Element ? currentNode.outerHTML : (currentNode.textContent ?? '');

      if (!foundHeading) {
        introContentHtml += nodeContent;
      } else {
        mainContentHtml += nodeContent;
      }
      currentNode = nextNode;
    }

    setIntroContent(foundHeading ? introContentHtml : content);
    setMainOverviewContent(foundHeading ? mainContentHtml : '');
    // Inject heading IDs into mainOverviewContent for the first overview tab (tabIndex 0)
    setMainOverviewContentWithIds(foundHeading ? injectHeadingIds(mainContentHtml, 0) : null);
    setOverviewHeading(firstHeadingText);
  }, [tabsData, currentTab]);

  if (!hydrated) {
    // Returns null on first render, so the client and server match
    return null;
  }

  const sortedTabsData = tabsData?.filter(
    (data: any) =>
      data.__component !== 'common.latest-update' &&
      data.__component !== 'course.latest-update' &&
      data.__component !== 'special.latest-update' &&
      data.__component !== 'exam.latest-update' &&
      data.__component !== 'common.seo' &&
      data.__component !== 'course.seo' &&
      data.__component !== 'special.seo' &&
      data.__component !== 'exam.seo',
  );
  const latestUpdateData = tabsData?.filter(
    (data: any) =>
      data.__component === 'common.latest-update' ||
      data.__component === 'course.latest-update' ||
      data.__component === 'special.latest-update' ||
      data.__component === 'exam.latest-update',
  );
  return (
    <section className="p-2">
      {sideBar === true && (
        <div className="mt-1 flex flex-col md:mb-2">
          <UpdateDateArticle dateTime={page?.updatedAt} updatedByUser={page?.updated_by_user} />
        </div>
      )}
      <div className=" mx-auto flex max-w-screen-xl gap-x-4">
        <div className=" mx-auto flex w-screen flex-1 flex-col gap-x-4 space-y-3 md:w-full md:space-y-4" key={page?.id}>
          {latestUpdateData?.[0] && (
            <div className="mt-2 md:mt-0">
              <PageData tabData={latestUpdateData?.[0]} pageData={page} key="latest-update" id={latestUpdateId} />
            </div>
          )}

          {isClient ? (
            <>
              {introContent && (
                <div className="ckeditorData !overflow-x-auto text-wrap px-2 font-inter text-sm md:text-base">
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(introContent) }} />
                </div>
              )}

              {showTableOfContent && (
                <Accordion
                  title="Table of Content"
                  titlePrimary
                  opened={openAccordionId === tableOfContentId}
                  data-id={tableOfContentId} // Changed to data-id which will be a valid HTML attribute
                  onToggle={() => handleAccordionToggle(tableOfContentId)}
                >
                  <div className="flex flex-col gap-x-4 bg-primary-extra-light p-2 md:p-5">
                    {(() => {
                      const uniqueLabels = new Set();
                      let firstOverviewFound = false;

                      // Helper to extract all headings (flat) from HTML content with IDs injected
                      const extractFlatHeadings = (html: string, tabIndex: number) => {
                        if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined')
                          return [];
                        const parser = new window.DOMParser();
                        const doc = parser.parseFromString(html, 'text/html');
                        const headings = Array.from(doc.querySelectorAll('h2, h3, h4, h5, h6'));
                        return headings.map((heading, idx) => {
                          const tag = heading.tagName.toLowerCase();
                          const text = heading.textContent ?? '';
                          const id = `tab-section-${tabIndex}-${idx}`;
                          return { id, name: text, tag };
                        });
                      };

                      return sortedTabsData
                        ?.map((table: any, index: any) => {
                          let label;
                          const isOverview = [
                            'common.new-overview',
                            'course.overview',
                            'special.overview',
                            'exam.overview',
                          ].includes(table?.__component);

                          // FAQ check
                          const isFaq =
                            table?.__component === 'common.faq' ||
                            table?.__component === 'course.faq' ||
                            table?.__component === 'special.faq' ||
                            table?.__component === 'exam.faq';

                          if (isOverview) {
                            if (!firstOverviewFound) {
                              label = overviewHeading ?? 'Overview';
                              firstOverviewFound = true;
                            } else {
                              label = table?.heading ?? 'Additional Overview';
                            }
                          } else if (isFaq) {
                            label = table?.heading ?? 'FAQs';
                          } else {
                            switch (table?.__component) {
                              case 'common.recommended-colleges':
                              case 'course.recommended-colleges':
                              case 'special.recommended-colleges':
                              case 'exam.recommended-colleges':
                                label = 'Recommended Colleges';
                                break;
                              case 'common.recommended-courses':
                              case 'course.recommended-courses':
                              case 'special.recommended-courses':
                              case 'exam.recommended-courses':
                                label = 'Recommended Courses';
                                break;
                              case 'common.recommended-exams':
                              case 'course.recommended-exams':
                              case 'special.recommended-exams':
                              case 'exam.recommended-exams':
                                label = 'Recommended Exams';
                                break;
                              case 'common.recommended-scholar':
                              case 'course.recommended-scholar':
                              case 'special.recommended-scholar':
                              case 'exam.recommended-scholar':
                                label = 'Recommended Scholarships';
                                break;
                              case 'common.recommended-countries':
                              case 'course.recommended-countries':
                              case 'special.recommended-countries':
                              case 'exam.recommended-countries':
                                label = 'Recommended Countries';
                                break;
                              default:
                                label = table?.heading;
                            }
                          }

                          if (!label || uniqueLabels.has(label)) return null;
                          uniqueLabels.add(label);

                          // Use the correct HTML for extracting headings:
                          let htmlForHeadings;
                          if (isOverview && index === 0 && mainOverviewContentWithIds) {
                            htmlForHeadings = mainOverviewContentWithIds;
                          } else {
                            // For other tabs, inject IDs as usual
                            htmlForHeadings = injectHeadingIds(table?.content ?? table?.Content, index);
                          }
                          let flatHeadings = extractFlatHeadings(htmlForHeadings, index);

                          // Remove the first heading if it matches the label (case-insensitive, trimmed)
                          if (
                            flatHeadings.length > 0 &&
                            flatHeadings[0].name.trim().toLowerCase() === (label ?? '').trim().toLowerCase()
                          ) {
                            flatHeadings = flatHeadings.slice(1);
                          }

                          return (
                            <div key={`${table?.id}-${index}`}>
                              <div className="flex gap-4 py-0.5 font-poppins text-xs md:text-base">
                                {/* <ListYellowBulletIcon /> */}
                                <div className="cursor-pointer font-medium text-primary">
                                  <button
                                    onClick={() => {
                                      const section = document.getElementById(`section-${index}`);
                                      if (section) {
                                        const headerOffset = 150; // Set to your fixed header height
                                        const elementPosition =
                                          section.getBoundingClientRect().top + window.pageYOffset;
                                        const offsetPosition = elementPosition - headerOffset;
                                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                                      }
                                    }}
                                  >
                                    {label}
                                  </button>
                                </div>
                              </div>
                              {/* Render flat headings for this tab */}
                              {flatHeadings.length > 0 && (
                                <ul className="list-disc pl-10">
                                  {flatHeadings.map((item: any) => (
                                    <li key={item.id} className="py-1 text-xs md:text-sm">
                                      <button
                                        className="cursor-pointer font-medium text-primary"
                                        onClick={() => {
                                          const heading = document.getElementById(item.id);
                                          if (heading) {
                                            const headerOffset = 150;
                                            const elementPosition =
                                              heading.getBoundingClientRect().top + window.pageYOffset;
                                            const offsetPosition = elementPosition - headerOffset;
                                            window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                                          }
                                        }}
                                      >
                                        {item.name}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })
                        .filter(Boolean);
                    })()}
                  </div>
                </Accordion>
              )}
            </>
          ) : (
            <div style={{ minHeight: 100 }} />
          )}

          {/* Render the rest of the tabs as usual */}
          {sortedTabsData?.map((tabData: any, index: number) => {
            let processedTabData = { ...tabData };
            const isFirstOverview =
              ['common.new-overview', 'course.overview', 'special.overview', 'exam.overview'].includes(
                tabData?.__component,
              ) &&
              overviewHeading &&
              index ===
                sortedTabsData.findIndex((t: any) =>
                  ['common.new-overview', 'course.overview', 'special.overview', 'exam.overview'].includes(
                    t?.__component,
                  ),
                );

            if (isFirstOverview && mainOverviewContentWithIds !== null) {
              // Use the processed mainOverviewContent with IDs for the first overview tab
              if (processedTabData.content !== undefined) {
                processedTabData.content = mainOverviewContentWithIds;
              } else if (processedTabData.Content !== undefined) {
                processedTabData.Content = mainOverviewContentWithIds;
              }
            } else if (processedTabData.content || processedTabData.Content) {
              const html = processedTabData.content ?? processedTabData.Content;
              const htmlWithIds = injectHeadingIds(html, index);
              if (processedTabData.content) {
                processedTabData.content = htmlWithIds;
              } else if (processedTabData.Content) {
                processedTabData.Content = htmlWithIds;
              }
            }
            return (
              <section
                id={`section-${index}`}
                key={`${tabData?.id}-${index}`}
                className="mx-auto my-4 w-full py-2"
              >
                <PageData tabData={processedTabData} pageData={page} id={tabData?.id} />
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PageDetailsTab;
